import { useCallback } from 'react'
import { useDispatch } from 'react-redux'

import { IInputFactoryProps, IUseInputFactoryReturnProps } from '../InputFactoryProps'
import { AdditionalFormNames, TAllBasedFieldsNames, FormNames } from '../../../../models/form/IFormDefinition'
import { CHANGE_ADDITIONAL_INCOME_INFO_FIELDS, CHANGE_INCOME_INFO_FIELDS, CLEAR_FIELD_VALIDATION_ERROR, SET_SINGLE_FIELD_VALUE, TOGGLE_FIELD_ON_BOOLEAN_CHANGE, ToggleFieldOnBooleanChangeActionType } from '../../../../store/actions/form-actions'
import { IDropdownBooleanOption, IDropdownChangeIncomeFieldsOption, IDropdownOption } from '../InputDropdown/InputDropdownProps'
import { IFieldBoolean } from '../../../../models/form/IFieldsDefinition'



const useInputFactoryLogic = (props: IInputFactoryProps): IUseInputFactoryReturnProps => {
   const DEBOUNCE_TIME = 500
 
   const { fieldDefinition: field, formName, currentFieldValidation, handleValidateSingleField } = props
   const dispatch = useDispatch()


   /**
    * Generyczna obsługa zdarzenia ON_BLUR dla WYBRANYCH KONTROLEK
    */
   const handleOnBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
      const newFieldValue = e.target.value // Pobieramy wartość z kontrolki
      handleValidateSingleField(formName as FormNames, field.name as Partial<TAllBasedFieldsNames>, newFieldValue, currentFieldValidation)
   }, [currentFieldValidation, field.name, formName, handleValidateSingleField])


   /**
    * Generyczna obsługa zdarzenia ON_PASTE dla WYBRANYCH KONTROLEK
    */
   const handlePaste = useCallback((e: React.ClipboardEvent<HTMLInputElement>) => {
      field.isPasteDisabled && e.preventDefault()
   }, [field.isPasteDisabled])



    /**
    * Funkcja do obsługi kontrolek w które użytkownik wpisuje wartości (TEXT LUB NUMBER). 
    * Zapisuje ona otrzymane wartościw stanie aplikacji oraz przeprowadza walidację
   */
   const handleSaveInputValueToStateAndValid = useCallback((newFieldValue: string | number) => {
      const rawFieldValue = typeof newFieldValue === "string" ? newFieldValue.trim() : newFieldValue // Jeśli wartość jest stringiem, usuwamy białe znaki z początku i końca

      dispatch({
         type: SET_SINGLE_FIELD_VALUE,
         payload: {
            formName,
            fieldName: field.name,
            newFieldState: rawFieldValue,
            newFieldValue: rawFieldValue,
         }
      })
      
      handleValidateSingleField(formName as FormNames, field.name as Partial<TAllBasedFieldsNames>, rawFieldValue, currentFieldValidation)
   }, [currentFieldValidation, dispatch, field.name, formName, handleValidateSingleField])
   


   /**
    * Specyficzna obługa zapisu do stanu dla kontrolki typu BOOLEAN
    */
   const handleOnBooleanChange = useCallback((newInputValue: IDropdownBooleanOption) => {
      if((field as IFieldBoolean).associatedFieldName !== undefined){ // Jeśli pole typu boolean posiada powiązane pole, wykonujemy specjalną akcję
         dispatch({
            type: TOGGLE_FIELD_ON_BOOLEAN_CHANGE,
            payload: {
               formName: formName as FormNames.IncomeInfoForm | AdditionalFormNames.AdditionalIncomeForm,
               actionType: newInputValue.value? ToggleFieldOnBooleanChangeActionType.ADD_FIELD : ToggleFieldOnBooleanChangeActionType.DELETE_FIELD,
               associatedFieldName: (field as IFieldBoolean).associatedFieldName,
            }
         })
      }

      dispatch({
         type: CLEAR_FIELD_VALIDATION_ERROR,
         payload: { formName, fieldName: field.name } // Jeśli kontrolka ma błąd, po wybraniu jakiejkolwiek wartości czyścimy go. Nie robimy tego przy zdarzeniu onBlur, ponieważ kontrolka go nie posiada
      })

      dispatch({
         type: SET_SINGLE_FIELD_VALUE,
         payload: {
            formName,
            fieldName: field.name,
            newFieldState: newInputValue,
            newFieldValue: newInputValue.value
         }
      })
   }, [dispatch, field, formName])



   /**
    * Specyficzna obługa zapisu do stanu dla kontrolki typu DROPDOWN
    */
   const handleOnDropdownChange = useCallback((newInputValue: IDropdownOption) => {
      dispatch({
         type: CLEAR_FIELD_VALIDATION_ERROR,
         payload: { formName, fieldName: field.name } // Jeśli kontrolka ma błąd, po wybraniu jakiejkolwiek wartości czyścimy go. Nie robimy tego przy zdarzeniu onBlur, ponieważ kontrolka go nie posiada
      })


      dispatch({
         type: SET_SINGLE_FIELD_VALUE,
         payload: {
            formName,
            fieldName: field.name,
            newFieldState: newInputValue,
            newFieldValue: newInputValue.value
         }
      })
   }, [dispatch, field.name, formName])


   /**
    * Specyficzna obługa zapisu do stanu dla kontrolki typu DROPDOWN_CHANGE_INCOME_TYPE_FIELD
    */
   const handleOnDropdownChangeIncomeFieldsChange = useCallback((newInputValue: IDropdownChangeIncomeFieldsOption) => {

      if(formName === FormNames.IncomeInfoForm) {
         dispatch({
            type: CHANGE_INCOME_INFO_FIELDS,
            payload: newInputValue.fields,
         })
      }
      else if (formName === AdditionalFormNames.AdditionalIncomeForm){
         dispatch({
            type: CHANGE_ADDITIONAL_INCOME_INFO_FIELDS,
            payload: newInputValue.fields,
         })
      }


      dispatch({
         type: CLEAR_FIELD_VALIDATION_ERROR,
         payload: { formName, fieldName: field.name } // Jeśli kontrolka ma błąd, po wybraniu jakiejkolwiek wartości czyścimy go. Nie robimy tego przy zdarzeniu onBlur, ponieważ kontrolka go nie posiada
      })
      
      dispatch({
         type: SET_SINGLE_FIELD_VALUE,
         payload: {
            formName,
            fieldName: field.name,
            newFieldState: newInputValue,
            newFieldValue: newInputValue.value
         }
      })
   }, [dispatch, field.name, formName])



   return {
      handleOnBlur,
      handlePaste,
      handleSaveInputValueToStateAndValid,
      handleOnBooleanChange,
      handleOnDropdownChange,
      handleOnDropdownChangeIncomeFieldsChange,

      debounceTime: DEBOUNCE_TIME,
   }
}

export default useInputFactoryLogic